import { useState, useMemo, useEffect } from 'react';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import { TOPGAMENUM } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { TableSelectionType } from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import { RecordItemType } from '@/pages/intelligence/components/SearchRecent/type';
import { getCountryFlag } from '@/pages/home/_config/countries';
import {
  updateShortLinkFilterId,
  updateTimeFilterValue,
  URL_FILTER_KEY,
} from '@intelligence/utils/urlPathCfg';
import {
  handleTableDefaultSelectGames,
  sortTableByMetric,
  filterGamesWithRank,
} from '@/pages/intelligence/utils/tableUtils';
import type {
  Game,
  UseTopChartsConfig,
  UseTopChartsResponse,
} from '@intelligence/pages/TopCharts/PCGames/_config/type';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';

/**
 * 获取前top的数据
 * @param list 源数据
 * @returns 前top的数据
 */
const handleTableToBar = <T extends Game>(list: T[], end = TOPGAMENUM): T[] => {
  if (!Array.isArray(list)) return [];
  return list.slice(0, end);
};

export default function useMetricsTableModel<E extends Game>(
  config: UseTopChartsConfig,
): UseTopChartsResponse<E> {
  const {
    entityType,
    defaultParams = {},
    getTableApiFun,
    getTrendApiFun,
    filterValue,
    setFilterVale,
    hasUrlShortLink,
    sortByFront,
    setIsVisualisLoading,
    fixedSelection = [],
    metricValueRangeTime,
  } = config || {};

  // console.log('useMetricsTableModel', defaultParams);

  // 查询表单对象
  const commonFetchParams = {
    entity_type: entityType,
    ...defaultParams,
  };

  const [fetchParams, setFetchParams] = useState<any>({
    ...commonFetchParams,
  });

  // 宽表总数据条数
  const [tableTotal, setTableTotal] = useState(0);
  // 宽表数据
  const [tableData, setTableData] = useState<E[]>([]);
  // echarts图表类型
  const [chartType, setChartType] = useState<ChartType>(ChartType.Bar);
  // 柱状图、饼图 数据源
  const [barOrPieData, setBarOrPieData] = useState<Game[]>([]);
  // 折线图数据源
  const [lineData, setLineData] = useState<any[]>([]);
  // 宽表勾选的数据
  const [defaultSelectionGames, setDefaultSelectionGames] = useState<
    TableSelectionType[]
  >([]);
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>(
    [],
  );

  // 是否重新操作过复选器提交
  const [isNewComfirm, setIsNewComfirm] = useState(true);
  // 是否重新勾选过游戏
  const [isNewCheckGame, setIsNewCheckGame] = useState(true);
  /** 真正的是否重新勾选游戏， 上面那个isNewCheckGame需要做优化处理 */
  const [operatedGame, setOperatedGame] = useState(false);
  const [showNoGameExtendModal, setShowNoGameExtendModal] = useState(false);
  const [tableListForClearSearch, setTableListForClearSearch] = useState([]);
  const [totalForClearSearch, setTotalForClearSearch] = useState(0);

  // 处理点击排序后勾选数据和请求折线的交互
  const updateGetGameData = (
    list: Game[],
    pageParams: any,
    isCheckOrSearch: boolean,
    isUpdateLineChart = true,
  ) => {
    let checkGameList = tableSelection;

    if (pageParams.page === 1) {
      // 过滤rank==0后的游戏可勾选展示可视化
      const filterNoRankGameList = filterGamesWithRank(list);
      /** 第一页时拿到默认前6个游戏作为游戏全选值, 筛选掉被固定的值 */
      const filterSelector = filterNoRankGameList
        .slice(0, TOPGAMENUM)
        ?.filter((t) => !fixedSelection?.includes(t?.app_id));
      // 固定选中的要一直被选中，将它的信息过滤出来
      const fixedSelectionGameMes: TableSelectionType[] =
        filterNoRankGameList?.filter((t) =>
          fixedSelection?.includes(t?.app_id),
        );
      // 过滤掉选中中有固定选中的，再合并固定的值，取TOPGAMENUM个后按rank排序
      const defaultSelectGames = [...fixedSelectionGameMes, ...filterSelector]
        ?.slice(0, TOPGAMENUM)
        ?.sort((a, b) => a?.rank - b?.rank);
      // 将实际被选中的值做游戏选择处理添加game_id
      const defaultGames = handleTableDefaultSelectGames(
        defaultSelectGames,
        'app_id',
      );
      setDefaultSelectionGames(defaultGames);
      /** 在第一页且没有勾选游戏时默认选中前6（游戏在排序或重新筛选时有更新） */
      if (!isCheckOrSearch) {
        setTableSelection(defaultGames);
        /** 分页排序请求接口时，可视化不改变 */
        const barOrPieDataTmp = handleTableToBar(defaultGames, TOPGAMENUM);
        setBarOrPieData([...barOrPieDataTmp]);
        checkGameList = defaultGames;
      }
    }

    if (chartType === ChartType.Line && isUpdateLineChart) {
      if (checkGameList.length > 0) {
        const idList = checkGameList.map((t) => t?.gameId);
        getLineChartData(pageParams, idList);
      } else {
        setLineData([]);
      }
    }
  };
  const getGameData = async (argums: {
    params: any;
    isCheckOrSearch: boolean;
    isUpdateLineChart: boolean;
    updateFilterValues: any;
    filterSaveName?: string;
    // 表格上方指标筛选器的值和列表
    metricFilterValue?: string[];
    tableColumns?: any[];
  }) => {
    const {
      params,
      isCheckOrSearch,
      isUpdateLineChart = true,
      updateFilterValues = filterValue,
      filterSaveName,
      metricFilterValue,
      tableColumns,
    } = argums;

    let pageParams = { ...params };
    // 无论请不请求接口，都先更新一下参数
    setFetchParams(pageParams);

    // filterValue region 值根据 fetchParams 的值进行变更
    if (
      pageParams.regions?.length === 1 &&
      pageParams.regions?.[0] === 'us' &&
      updateFilterValues?.[filterKeyMap.REGION]?.regions?.length === 1 &&
      updateFilterValues?.[filterKeyMap.REGION]?.regions?.[0] === 'global'
    ) {
      const defaultUSRegion = {
        regions: ['us'],
        markets: ['us'],
        type: 'market',
      };
      // updateFilterValues[filterKeyMap.REGION] = defaultUSRegion;
      setFilterVale?.((prev: Record<string, any>) => {
        return {
          ...prev,
          [filterKeyMap.REGION]: defaultUSRegion,
        };
      });
    }

    if (
      pageParams.regions?.length === 1 &&
      pageParams.regions[0] === 'global' &&
      updateFilterValues?.[filterKeyMap.REGION]?.regions?.length === 1 &&
      updateFilterValues?.[filterKeyMap.REGION]?.regions?.[0] === 'us'
    ) {
      const defaultGlobalRegion = {
        regions: ['global'],
        markets: ['global'],
        type: 'market',
      };

      // updateFilterValues[filterKeyMap.REGION] = defaultGlobalRegion;

      setFilterVale?.((prev: Record<string, any>) => {
        return {
          ...prev,
          [filterKeyMap.REGION]: defaultGlobalRegion,
        };
      });
    }

    // 当时间为空时，等时间返回再更新，否则直接写token到接口
    const isUpdateShortLinkNow = !!params?.start_date && !!params?.end_date;
    // 时间如果是空的，则需要更新时间筛选器
    const needUpdateTime = !params?.start_date || !params?.end_date;
    if (isUpdateShortLinkNow && hasUrlShortLink) {
      updateShortLinkFilterId({
        [URL_FILTER_KEY]: updateFilterValues,
        metricFilterValue,
        tableColumns,
      });
    }

    const isSearch = pageParams?.unified_ids?.length > 0;
    try {
      if (JSON.stringify(pageParams?.source_metrics) === '{}') {
        throw new Error('该筛选条件导致没有任何指标被选择！');
      }
      setTableData([]);
      const resData = await getTableApiFun(pageParams);
      const list = resData?.data?.rank_au_list || [];
      const total = parseInt(resData?.data?.total || 0, 10);
      const req = resData?.req;

      if (sortByFront) {
        // 请求接口后需要前端排序
        const { tableList: sortList, params: paramsSort } = handleSortByFront({
          list,
          params: pageParams,
          isCheckGame: isCheckOrSearch,
          isUpdateLineChart,
        });
        pageParams = { ...pageParams, ...paramsSort };
        setTableData(sortList);
        setTableTotal(sortList?.length);
      } else {
        updateGetGameData(list, pageParams, isCheckOrSearch, isUpdateLineChart);
        setTableData(list || []);
        setTableTotal(total || list?.length);
      }
      if (!isSearch) {
        // 有game_code表示是搜索，则不存页面数据
        setTableListForClearSearch(list || []);
        setTotalForClearSearch(total || list?.length);
      }
      if (req) {
        // 如果数据没有正常返回则不设置
        const newFetchParams = {
          ...pageParams,
          date_type: req?.date_type || 'monthly',
          start_date: req?.start_date || '',
          end_date: req?.end_date || '',
          /** 当筛选国家数据不一样时，表格要以当前第一个有数据的排序，后端返回 */
          order_metric: req?.order_metric || pageParams?.order_metric,
          order_source: req?.order_source || pageParams?.order_source,
          order: req?.order || pageParams?.order,
          ratio_type: req?.ratio_type || pageParams?.ratio_type,
          // 如果是搜索或前端排序的时候，则不回填
          filter_ids:
            isSearch || sortByFront ? pageParams?.filter_ids : req?.filter_ids,

          // 第一页的Top100，传id list后端用于优化下载接口速度
          firstPageTopList:
            req?.page === 1 && req?.page_size === 100
              ? list?.map((item: any) => item?.app_id || '')
              : [],
        };
        setFetchParams(newFetchParams);

        metricValueRangeTime.current = {
          dateType: newFetchParams.date_type,
          startDate: newFetchParams.start_date,
          endDate: newFetchParams.end_date,
        };

        // console.log('metricValueRangeTimemetricValueRangeTime', metricValueRangeTime);
        // console.log('newFetchParamsnewFetchParams', newFetchParams);
        // console.log('needUpdateTimeneedUpdateTime', needUpdateTime);

        // 获取到时间后更新筛选器的值，并更新url短链接
        if (needUpdateTime && req?.start_date && req?.end_date) {
          const newFilterValue = updateTimeFilterValue({
            // hasUrlShortLinkVal,
            haveFilterSaveBtn: true,
            filterSaveName,
            filterValue: updateFilterValues,
            timeFilterValues: {
              type: newFetchParams.date_type,
              value: [newFetchParams.start_date, newFetchParams.end_date],
            },
          });
          // console.log('needUpdateTimeneedUpdateTimenewFilterValue', newFilterValue);
          if (!newFilterValue[filterKeyMap.REGION]) {
            // region 默认值，避免无值，导致无法判断国家地区选择器是否有变动
            newFilterValue[filterKeyMap.REGION] = {
              regions: ['global'],
              markets: ['global'],
              type: 'market',
            };
          }
          // console.log('newFilterValuenewFilterValue56565656',newFilterValue)
          setFilterVale(newFilterValue);
          // 如果是时间为空等时间返回更新 且有短链接功能，则更新短链接
          if (!isUpdateShortLinkNow && hasUrlShortLink) {
            updateShortLinkFilterId({
              [URL_FILTER_KEY]: newFilterValue,
              metricFilterValue,
              tableColumns,
            });
          }
        }
      }
    } catch (e) {
      console.error(e);
      // 如果是搜索的情况接口报错，不需要清空可视化
      setTableData([]);
      setTableTotal(0);
      setTableListForClearSearch([]);
      setTotalForClearSearch(0);
      if (!isSearch) {
        setBarOrPieData([]);
        setLineData([]);
        setTableSelection([]);
      }
      metricValueRangeTime.current = {
        dateType: pageParams.date_type,
        startDate: pageParams.start_date,
        endDate: pageParams.end_date,
      };
    } finally {
    }
  };

  const getLineChartData = async (params: any, idList: string[]) => {
    if (idList?.length > 0) {
      try {
        setIsVisualisLoading(true);
        const newFetchParams = {
          ...params,
          unified_ids: idList,
          data_method: 'date',
        };
        setLineData([]);
        const resData = await getTrendApiFun({
          ...newFetchParams,
        });
        setLineData(resData?.data?.metric_list || []);
        setIsNewComfirm(false);
        setIsNewCheckGame(false);
      } catch (e) {
        setLineData([]);
        console.error(e);
      } finally {
        setIsVisualisLoading(false);
      }
    } else {
      // 未勾选任何游戏，不请求，返回为空数据
      setLineData([]);
    }
  };

  const isNodata = useMemo(() => {
    if (tableTotal < 1 || tableSelection.length < 1) {
      return true;
    }
    return false;
  }, [tableTotal, tableSelection]);

  const handleClearSearch = () => {
    setFetchParams({ ...fetchParams, unified_ids: [] });
    setTableData(tableListForClearSearch);
    setTableTotal(totalForClearSearch);
  };

  const transTableDataForSearch = (res: any) => {
    const resultList = res?.data?.rank_au_list;
    if (!Array.isArray(resultList) || !resultList?.length) {
      return [];
    }
    const tranksList: RecordItemType[] = resultList.map((item) => ({
      id: item?.app_id,
      imgSrc: item?.cover,
      lineOneStr: item?.name,
      lineTwoImg: getCountryFlag(item?.publisher_addr),
      lineTwoImgName: item?.publisher_addr,
      // imageType: '',
      lineTwoStr: item?.publisher,
    }));

    return tranksList;
  };

  const handleSortByFront = (args: {
    params: any;
    isCheckGame: boolean;
    isUpdateLineChart: boolean;
    list: Game[];
  }) => {
    const {
      list = tableData,
      params,
      isCheckGame,
      isUpdateLineChart = true,
    } = args;

    setFetchParams(params);
    const sortParams = {
      ratioType: params?.ratio_type,
      sortVal: params?.order,
      sortMetric: params?.order_metric,
      sortSource: params?.order_source,
    };

    const getMetacriticValueFun = (cvm: Game) => {
      const metricsList = Array.isArray(cvm?.metric_list)
        ? cvm?.metric_list
        : [];
      const metricHistory = metricsList.find(
        (item) => item?.metric === 'metacritic' && item?.title === 'meta_score',
      );
      return metricHistory?.history?.find(
        (item) => item?.device === params?.order_device,
      );
    };
    const isSortMetric =
      sortParams.sortMetric === 'meta_score' &&
      params.order_source === 'metacritic';
    const sortTable = sortTableByMetric({
      tableData: list,
      sortParams,
      growthProperty: 'growth_ratio',
      listProperty: 'metric_list',
      metricProperty: 'title',
      sourceProperty: 'metric',
      // 评分的排序比较特殊
      getMetricValueFun: isSortMetric ? getMetacriticValueFun : undefined,
    });
    // 建议没有排名的放在最后
    const tableUpdateRank = sortTable?.map((t, idx) => ({
      ...t,
      rank: idx + 1,
    }));
    updateGetGameData(tableUpdateRank, params, isCheckGame, isUpdateLineChart);
    setTableData(tableUpdateRank);
    // 前端排序后的Id的回填到参数用于有序下载
    const newFetchParams = {
      ...params,
      filter_ids: tableUpdateRank?.map((t) => t?.app_id),
    };
    setFetchParams(newFetchParams);
    return { tableList: tableUpdateRank, params: newFetchParams };
  };

  return {
    fetchParams,
    tableTotal,
    tableData,
    chartType,
    setChartType,
    barOrPieData,
    lineData,
    getGameData,
    getLineChartData,
    defaultSelectionGames,
    isNewComfirm,
    setIsNewComfirm,
    isNewCheckGame,
    setIsNewCheckGame,
    tableSelection,
    setBarOrPieData,
    operatedGame,
    setOperatedGame,
    setTableSelection,
    isNodata,
    showNoGameExtendModal,
    setShowNoGameExtendModal,
    handleClearSearch,
    transTableDataForSearch,
    handleSortByFront,
  };
}
